import buildFixtoolIntakeTemplate from "../../helpers/intakeForms/buildFixtoolIntakeTemplate";
import { useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import notDeleted from "../../helpers/notDeleted.js";
import { useFirestoreOrderBy, useFirestore } from "../../firebase/useFirestore";
import { db, timestamp } from "../../firebase/config";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { client } from "../../hooks/Client";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { Auth } from "../../StateManagment/Auth";
import { useHistory } from "react-router-dom";
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import CopyLink from "../../components/common/CopyLink";
import PageHeader from "../../components/layout/PageHeader.jsx";
import useSettings from "../../hooks/Settings";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableDragHandleCell from "../../components/common/table/TableDragHandleCell.jsx";
import TableTitleCell from "../../components/common/table/TableTitleCell.jsx";
import Modal from "../../components/common/Modal";
import plusButton from "../../images/icons/plus-icon.png";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import Tooltip from "../../components/common/Tooltip";
import DeleteModal from "../../components/common/DeleteModal";
import buildIntakeFormCopy from "../../helpers/intakeForms/buildIntakeFormCopy";
import buildEnergyPovertyIntakeTemplate from "../../helpers/intakeForms/buildEnergyPovertyIntakeTemplate";
import clickableRowProps from '../../helpers/a11y/clickableRowProps';

const Intakeforms = () => {
  // Context
  const [, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // State
  const [dragArrayIntakes, setDragArrayIntakes] = useState([]);
  const [showAddContainer, setShowAddContainer] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState("");
  const [deleteFormId, setDeleteFormId] = useState("");
  const [deleteName, setDeleteName] = useState("");

  // Hooks
  const history = useHistory();
  const { housevisits, personasOn } = useSettings();

  // Firestore
  const intakeForms = useFirestoreOrderBy("IntakeForms", "Position", "asc");
  const personas = useFirestore("Personas");
  // Soft delete (ADR-008): verwijderde huishoudens niet aanbieden.
  const allAdresses = useFirestore("Adresses");
  const adresses = useMemo(
    () => (Array.isArray(allAdresses) ? allAdresses : []).filter(notDeleted),
    [allAdresses]
  );

  useEffect(() => {
    setDragArrayIntakes(intakeForms);
  }, [intakeForms]);

  // Helpers
  const normalize = (v) => (v || "").trim();

  const getEntityLabel = (selectedId) => {
    if (!selectedId) return "";
    const p = (personas || []).find((x) => x.ID === selectedId);
    const a = (adresses || []).find((x) => x.ID === selectedId);
    return p?.Name || a?.Title || selectedId;
  };

  const typeLabel = (t) => {
    switch (normalize(t)) {
      case "intake":
        return "Intakeformulier";
      case "standard":
        return "Standaardformulier";
      default:
        return "—";
    }
  };

  const targetLabel = (t) => {
    switch (normalize(t)) {
      case "persona":
        return "Deelnemer";
      case "adress":
        return "Huishouden";
      default:
        return "—";
    }
  };

  // Determine which columns are necessary (per your requirement)
  const showTypeCol = useMemo(() => {
    return (dragArrayIntakes || []).some((f) => !!normalize(f?.FormType));
  }, [dragArrayIntakes]);

  const showTargetCol = useMemo(() => {
    // target makes sense for both types, but only show if at least one has it set
    return (dragArrayIntakes || []).some((f) => !!normalize(f?.FormTarget));
  }, [dragArrayIntakes]);

  const showSelectedCol = useMemo(() => {
    // selected only relevant for standard forms; show if any form is standard
    // OR any has FormSelected set
    return (dragArrayIntakes || []).some(
      (f) => normalize(f?.FormType) === "standard" || !!normalize(f?.FormSelected)
    );
  }, [dragArrayIntakes]);

  // Drag & drop: IntakeForms
  const handleDragEndIntakes = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayIntakes);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArrayIntakes(items);

    items.forEach((item, idx) => {
      db.collection("IntakeForms").doc(item.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  const titleHandler = (e) => {
    const docid = e.target.dataset.docid;
    const newTitle = e.target.value;

    db.collection("IntakeForms")
      .doc(docid)
      .update({ Title: newTitle })
      .then(() => setSaved("flex"));
  };

  // Open the confirmation modal for the clicked form.
  const deleteModalHandler = (e) => {
    setDeleteDocid(e.currentTarget.dataset.docid);
    setDeleteFormId(e.currentTarget.dataset.formid);
    setDeleteName(e.currentTarget.dataset.deletename);
    setDeleteModal(true);
  };

  // Runs after the user confirms in the DeleteModal. Soft delete (ADR-008) —
  // the central useFirestore filter hides the form and its linked data.
  const deleteIntakeForm = async () => {
    if (!deleteDocid) return;

    // IntakeForms + IntakeFormFields use the boolean form; MultipleQuestionOptions
    // stays a string ("true") — countActiveOptions compares Deleted === "true".
    const softDelete = { Deleted: true, DeletedAt: timestamp, DeletedBy: auth?.ID || "" };
    const softDeleteOption = { ...softDelete, Deleted: "true" };

    // One batch keeps the cascade atomic. A form + its fields + options stays
    // well under Firestore's 500-write cap for any realistic intake form.
    const batch = db.batch();
    batch.update(db.collection("IntakeForms").doc(deleteDocid), softDelete);

    // Cascade to the form's fields and their answer options. These are .get()
    // bypass reads, so filter already-deleted docs ourselves to avoid rewrites.
    if (deleteFormId) {
      const fieldsSnap = await db
        .collection("IntakeFormFields")
        .where("CompagnyID", "==", client)
        .where("FormId", "==", deleteFormId)
        .get();
      const liveFields = fieldsSnap.docs
        .map((d) => ({ ...d.data(), docid: d.id }))
        .filter(notDeleted);

      // Fetch every field's options concurrently, then queue the soft-deletes.
      const optionSnaps = await Promise.all(
        liveFields.map((field) =>
          field.ID
            ? db.collection("MultipleQuestionOptions").where("Field", "==", field.ID).get()
            : null
        )
      );

      liveFields.forEach((field, i) => {
        batch.update(db.collection("IntakeFormFields").doc(field.docid), softDelete);

        const snap = optionSnaps[i];
        if (!snap) return;
        snap.docs
          .map((d) => ({ ...d.data(), docid: d.id }))
          .filter(notDeleted)
          .forEach((opt) => {
            batch.update(
              db.collection("MultipleQuestionOptions").doc(opt.docid),
              softDeleteOption
            );
          });
      });
    }

    await batch.commit();
    setSaved("flex");
  };

  // Duplicate an existing form with all its fields and answer options.
  const duplicateIntakeForm = async (form) => {
    if (!form?.ID) return;

    // Bypass reads (.get) skip the central soft-delete filter — buildIntakeFormCopy
    // drops soft-deleted fields/options itself.
    const fieldsSnap = await db
      .collection("IntakeFormFields")
      .where("CompagnyID", "==", client)
      .where("FormId", "==", form.ID)
      .get();
    const fields = fieldsSnap.docs.map((d) => ({ ...d.data(), docid: d.id }));

    // Fetch every field's options concurrently; buildIntakeFormCopy maps each
    // option back to its field via opt.Field, so order doesn't matter.
    const optionSnaps = await Promise.all(
      fields.map((field) =>
        field?.ID
          ? db.collection("MultipleQuestionOptions").where("Field", "==", field.ID).get()
          : null
      )
    );
    const options = optionSnaps
      .filter(Boolean)
      .flatMap((snap) => snap.docs.map((d) => ({ ...d.data(), docid: d.id })));

    const copy = buildIntakeFormCopy({
      form,
      fields,
      options,
      client,
      timestamp,
      userId: auth?.ID || "",
      position: (intakeForms?.length || 0) + 1,
    });

    // One batch keeps the copy atomic — well under Firestore's 500-write cap
    // for any realistic intake form.
    const batch = db.batch();
    batch.set(db.collection("IntakeForms").doc(), copy.form);
    copy.fields.forEach((f) => batch.set(db.collection("IntakeFormFields").doc(), f));
    copy.options.forEach((o) => batch.set(db.collection("MultipleQuestionOptions").doc(), o));

    await batch.commit();
    setSaved("flex");
  };

  // Add: blank form
  const createBlankForm = () => {
    setShowAddContainer(false);
    db.collection("IntakeForms")
      .add({
        ID: uuid(),
        Title: "",
        CompagnyID: client,
        Compagny: client,
        Position: (intakeForms?.length || 0) + 1,
        Timestamp: timestamp,
        User: auth?.ID || "",
        DataItems: [],
        FormType: "",
        FormTarget: "",
        FormSelected: "",
      })
      .then(() => setSaved("flex"));
  };

  // Add: FIXTool template form, gekoppeld aan de vaste gegevens (T13)
  const createTemplateForm = async () => {
    setShowAddContainer(false);

    const formID = uuid();

    await db.collection("IntakeForms").add({
      ID: formID,
      Title: "Template: Intakeformulier FIXtool",
      CompagnyID: client,
      Compagny: client,
      Position: (intakeForms?.length || 0) + 1,
      Timestamp: timestamp,
      User: auth?.ID || "",
      DataItems: [],
      FormType: "intake",
      FormTarget: "adress",
      FormSelected: "",
    });

    // De vragen komen uit het schema van de vaste gegevens: naam, adres,
    // contact en woning hangen aan hun vaste gegeven (T13).
    // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
    const fields = buildFixtoolIntakeTemplate();

    for (let i = 0; i < fields.length; i++) {
      const fieldID = uuid();

      await db.collection("IntakeFormFields").add({
        ID: fieldID,
        Title: fields[i].Title,
        Type: fields[i].Type,
        Required: fields[i].Required,
        ...(fields[i].FixedFieldKey ? { FixedFieldKey: fields[i].FixedFieldKey } : {}),
        Position: i + 1,
        FormId: formID,
        CompagnyID: client,
        Compagny: client,
        Timestamp: timestamp,
      });

      // Antwoordopties van een keuzevraag (type woning, woningvorm).
      const options = fields[i].Options || [];
      for (let o = 0; o < options.length; o++) {
        await db.collection("MultipleQuestionOptions").add({
          ID: uuid(),
          Option: options[o],
          Question: fields[i].Title,
          Field: fieldID,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: o + 1,
          Deleted: "false",
          DeletedAt: "",
        });
      }
    }

    setSaved("flex");
    history.push(`/${client}/intakeformbuilder/${formID}`);
  };

  // Add: Persona template form (new medewerker intake)
  const createPersonaTemplateForm = async () => {
    setShowAddContainer(false);

    const formID = uuid();

    const fields = [
      { Title: "Voornaam",                   Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Achternaam",                 Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Geboortedatum",              Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Geslacht",                   Type: "text",   Category: "data",     Required: "false" },
      { Title: "Straatnaam",                 Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Huisnummer",                 Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Postcode",                   Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Woonplaats",                 Type: "text",   Category: "data",     Required: "false" },
      { Title: "Telefoonnummer",             Type: "text",   Category: "data",     Required: "true"  },
      { Title: "Email",                      Type: "text",   Category: "data",     Required: "true"  },
      { Title: "BSN",                        Type: "text",   Category: "data",     Required: "false" },
      { Title: "Land van herkomst",          Type: "text",   Category: "data",     Required: "false" },
      { Title: "Allergie\u00ebn",            Type: "text",   Category: "risk",     Required: "false" },
      { Title: "Bijzonderheden",             Type: "text",   Category: "specific", Required: "false" },
      { Title: "Identiteitsdocument",        Type: "upload", Category: "document", Required: "false" },
      { Title: "VOG",                        Type: "upload", Category: "document", Required: "false" },
    ];

    const batch = db.batch();

    const formRef = db.collection("IntakeForms").doc();
    batch.set(formRef, {
      ID: formID,
      Title: "Template: Intakeformulier nieuwe medewerker",
      CompagnyID: client,
      Compagny: client,
      Position: (intakeForms?.length || 0) + 1,
      Timestamp: timestamp,
      User: auth?.ID || "",
      DataItems: [],
      FormType: "intake",
      FormTarget: "persona",
      FormSelected: "",
    });

    fields.forEach((f, i) => {
      const fieldRef = db.collection("IntakeFormFields").doc();
      batch.set(fieldRef, {
        ID: uuid(),
        Title: f.Title,
        Type: f.Type,
        Category: f.Category,
        Required: f.Required,
        Position: i + 1,
        FormId: formID,
        CompagnyID: client,
        Compagny: client,
        Timestamp: timestamp,
      });
    });

    await batch.commit();

    setSaved("flex");
    history.push(`/${client}/intakeformbuilder/${formID}`);
  };

  // Add: energy poverty impact template (household intake)
  const createEnergyPovertyTemplateForm = async () => {
    setShowAddContainer(false);

    const template = buildEnergyPovertyIntakeTemplate({
      client,
      timestamp,
      userId: auth?.ID || "",
      position: (intakeForms?.length || 0) + 1,
    });

    // One batch keeps the form + fields + options atomic — ~30 writes, well
    // under Firestore's 500-write cap.
    const batch = db.batch();
    batch.set(db.collection("IntakeForms").doc(), template.form);
    template.fields.forEach((f) =>
      batch.set(db.collection("IntakeFormFields").doc(), f)
    );
    template.options.forEach((o) =>
      batch.set(db.collection("MultipleQuestionOptions").doc(), o)
    );

    await batch.commit();

    setSaved("flex");
    history.push(`/${client}/intakeformbuilder/${template.form.ID}`);
  };

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader heading={"Formulieren"} subHeading={""} description={``} AI={false} />

        <ReadOnlyGuard edit="forms.edit">
          {(canEdit) => (
          <Table
            columns={[
              { key: 'drag', label: '', searchable: false },
              { key: 'title', label: 'TITEL' },
              ...(showTypeCol ? [{ key: 'type', label: 'TYPE', searchable: false }] : []),
              ...(showTargetCol ? [{ key: 'target', label: 'DOEL', searchable: false }] : []),
              ...(showSelectedCol ? [{ key: 'selected', label: 'GESELECTEERD', searchable: false }] : []),
              { key: 'link', label: 'LINK', searchable: false },
              { key: 'qrcode', label: 'QR-CODE', searchable: false },
              { key: 'print', label: 'PRINTEN', searchable: false },
              { key: 'duplicate', label: 'KOPIËREN', searchable: false },
              { key: 'delete', label: 'VERWIJDEREN', searchable: false },
            ]}
            items={dragArrayIntakes || []}
            draggable
            droppableId="intakeForms"
            onDragEnd={handleDragEndIntakes}
            getItemKey={(item) => item.ID}
            searchAccessor={(item) => item?.Title || ''}
            searchPlaceholder="Zoeken op titel"
            emptyMessage="Geen formulieren gevonden."
            addRow={canEdit ? {
              toolTipContent: 'Formulier toevoegen',
              onClick: () => setShowAddContainer(!showAddContainer),
            } : undefined}
            renderRow={(item) => {
            const t = normalize(item?.FormType);
            const target = normalize(item?.FormTarget);
            const selected = normalize(item?.FormSelected);

            return (
              <>
                <TableDragHandleCell>
                  <DragIndicatorOutlinedIcon />
                </TableDragHandleCell>
                <TableTitleCell
                  value={item.Title}
                  to={`/${client}/intakeformbuilder/${item.ID}`}
                  onBlur={titleHandler}
                  docid={item.docid}
                  placeholder="Formulier zonder titel"
                />
                {showTypeCol ? <TableCell>{typeLabel(t)}</TableCell> : null}
                {showTargetCol ? (
                  <TableCell>{target ? targetLabel(target) : "—"}</TableCell>
                ) : null}
                {showSelectedCol ? (
                  <TableCell>
                    {t === "standard"
                      ? selected
                        ? getEntityLabel(selected)
                        : "—"
                      : "—"}
                  </TableCell>
                ) : null}
                <TableCell>
                  <CopyLink link={`/${client}/intakeformopen/${item.ID}/open`} showOpen />
                </TableCell>
                <TableCell>
                  <QrCodeOutlinedIcon
                    className="table-delete-icon"
                    onClick={() =>
                      history.push(`/${client}/qrcode/${item.ID}/intakeform`)
                    }
                  />
                </TableCell>
                <TableCell>
                  {/* Geen canEdit-poort: printen leest alleen. Wie het
                      formulier mag zien, mag het ook op papier meenemen. */}
                  <Tooltip content="Formulier printen" top="-60px">
                    <PrintOutlinedIcon
                      className="table-delete-icon"
                      onClick={() =>
                        history.push(`/${client}/printintakeform/${item.ID}`)
                      }
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <Tooltip content="Formulier kopiëren" top="-60px">
                      <FileCopyOutlinedIcon
                        className="table-delete-icon"
                        onClick={() => duplicateIntakeForm(item)}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <DeleteOutlinedIcon
                      className="table-delete-icon"
                      data-docid={item.docid}
                      data-formid={item.ID}
                      data-deletename={item.Title || "dit formulier"}
                      onClick={deleteModalHandler}
                    />
                  )}
                </TableCell>
              </>
            );
          }}
          />
          )}
        </ReadOnlyGuard>

        <Modal
          modalOpen={showAddContainer}
          setModalOpen={setShowAddContainer}
          pageHeader={'Formulier toevoegen'}
          hideConfirm={true}
          content={
            <ReadOnlyGuard edit="forms.edit">
              {(canEdit) => (
                <>
                  <div className="add-options-container-item-container" {...clickableRowProps(canEdit ? () => { setShowAddContainer(false); createBlankForm(); } : undefined)}>
                    <img src={plusButton} alt="" />
                    <p>Eigen formulier</p>
                  </div>
                  {housevisits && (
                    <div className="add-options-container-item-container" {...clickableRowProps(canEdit ? () => { setShowAddContainer(false); createTemplateForm(); } : undefined)}>
                      <img src={plusButton} alt="" />
                      <p>Template: intakeformulier FIXtool</p>
                    </div>
                  )}
                  {housevisits && (
                    <div className="add-options-container-item-container" {...clickableRowProps(canEdit ? () => { setShowAddContainer(false); createEnergyPovertyTemplateForm(); } : undefined)}>
                      <img src={plusButton} alt="" />
                      <p>Template: Impact meten Energiearmoede</p>
                    </div>
                  )}
                  {personasOn === 'flex' && (
                    <div className="add-options-container-item-container" {...clickableRowProps(canEdit ? () => { setShowAddContainer(false); createPersonaTemplateForm(); } : undefined)}>
                      <img src={plusButton} alt="" />
                      <p>Template: Intakeformulier nieuwe medewerker</p>
                    </div>
                  )}
                </>
              )}
            </ReadOnlyGuard>
          }
        />

        <DeleteModal
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          deleteItem={deleteIntakeForm}
          deleteName={deleteName}
          softDelete
          linkedNote="De vragen en antwoordopties van dit formulier gaan mee."
        />
      </div>
    </div>
  );
};

export default Intakeforms;
